import React, { useState, useEffect } from 'react';
import { Search as SearchIcon, Calendar, MapPin, FileText, ExternalLink } from 'lucide-react';

function Search() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNews, setFilteredNews] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Track window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sample RTO news data with proper structure
  const rtoNewsData = [
    {
      id: 1,
      title: "New Vehicle Registration Process Simplified",
      description: "RTO introduces online vehicle registration system to reduce waiting time and improve efficiency for citizens.",
      category: "registration",
      location: "Mumbai RTO",
      date: "2025-01-15",
      source: "Transport Department",
      tags: ["digital", "efficiency", "online-services"],
      priority: "high"
    },
    {
      id: 2,
      title: "Driving License Renewal Extended Period",
      description: "Grace period for driving license renewal extended by 6 months due to system upgrades across all RTOs.",
      category: "license",
      location: "All RTOs",
      date: "2025-01-10",
      source: "Ministry of Transport",
      tags: ["renewal", "extension", "grace-period"],
      priority: "medium"
    },
    {
      id: 3,
      title: "Electric Vehicle Registration Incentives",
      description: "Special discount on registration fees for electric vehicles to promote eco-friendly transportation.",
      category: "registration",
      location: "Delhi RTO",
      date: "2025-01-08",
      source: "Delhi Transport Authority",
      tags: ["electric-vehicle", "incentives", "eco-friendly"],
      priority: "high"
    },
    {
      id: 4,
      title: "New Traffic Rules Implementation",
      description: "Updated traffic violation penalties and new safety regulations come into effect from next month.",
      category: "traffic-rules",
      location: "Bangalore RTO",
      date: "2025-01-05",
      source: "Karnataka RTO",
      tags: ["traffic-rules", "penalties", "safety"],
      priority: "high"
    },
    {
      id: 5,
      title: "RTO Holiday Schedule Updated",
      description: "Complete list of RTO office closures and holiday schedules for the upcoming quarter released.",
      category: "announcements",
      location: "All RTOs",
      date: "2025-01-03",
      source: "Central Transport Ministry",
      tags: ["holidays", "schedule", "announcements"],
      priority: "low"
    },
    {
      id: 6,
      title: "Digital Driving Test Implementation",
      description: "New digital driving test system launched to ensure fair and standardized testing across all centers.",
      category: "license",
      location: "Chennai RTO",
      date: "2025-01-01",
      source: "Tamil Nadu Transport",
      tags: ["digital-test", "standardization", "technology"],
      priority: "medium"
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'registration', label: 'Vehicle Registration' },
    { value: 'license', label: 'Driving License' },
    { value: 'traffic-rules', label: 'Traffic Rules' },
    { value: 'announcements', label: 'Announcements' }
  ];

  useEffect(() => {
    filterNews();
  }, [searchTerm, selectedCategory]);

  const filterNews = () => {
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      let filtered = rtoNewsData;

      // Filter by category
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(news => news.category === selectedCategory);
      }

      // Filter by search term
      if (searchTerm.trim()) {
        filtered = filtered.filter(news =>
          news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          news.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          news.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
          news.location.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Sort by priority and date
      filtered.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return new Date(b.date) - new Date(a.date);
      });

      setFilteredNews(filtered);
      setLoading(false);
    }, 300);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: windowWidth <= 768 ? '10px' : '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        marginBottom: '30px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{
            fontSize: windowWidth <= 768 ? '1.8rem' : windowWidth <= 1024 ? '2.2rem' : '2.5rem',
            fontWeight: 'bold',
            color: '#1e293b',
            marginBottom: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center',
            lineHeight: '1.2'
          }}>
            RTO News & Updates
          </h1>
          <p style={{
            fontSize: windowWidth <= 768 ? '0.95rem' : '1.1rem',
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto',
            padding: windowWidth <= 768 ? '0 10px' : '0',
            lineHeight: '1.5'
          }}>
            Stay updated with the latest news, announcements, and updates from Regional Transport Offices across India
          </p>
        </div>

        {/* Search and Filter Controls */}
        <div style={{
          display: 'flex',
          gap: windowWidth <= 768 ? '15px' : '20px',
          marginBottom: '30px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          flexDirection: windowWidth <= 768 ? 'column' : 'row',
          alignItems: windowWidth <= 768 ? 'stretch' : 'center'
        }}>
          {/* Search Input */}
          <div style={{
            position: 'relative',
            minWidth: windowWidth <= 768 ? '100%' : '300px',
            flex: windowWidth <= 768 ? 'none' : '1',
            maxWidth: windowWidth <= 768 ? '100%' : '400px'
          }}>
            <SearchIcon style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              width: '20px',
              height: '20px'
            }} />
            <input
              type="text"
              placeholder={windowWidth <= 768 ? "Search RTO news..." : "Search RTO news, locations, or topics..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: windowWidth <= 768 ? '14px 14px 14px 45px' : '12px 12px 12px 45px',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: windowWidth <= 768 ? '16px' : '16px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              }}
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: windowWidth <= 768 ? '14px 16px' : '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer',
              minWidth: windowWidth <= 768 ? '100%' : '200px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              boxSizing: 'border-box'
            }}
          >
            {categories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        {/* Results Summary */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          color: '#64748b',
          fontSize: '1rem'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #e2e8f0',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Searching...
            </div>
          ) : (
            `Found ${filteredNews.length} news ${filteredNews.length === 1 ? 'item' : 'items'}`
          )}
        </div>
      </div>

      {/* News Grid */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 768 
          ? '1fr' 
          : window.innerWidth <= 1024 
            ? 'repeat(auto-fill, minmax(350px, 1fr))'
            : 'repeat(auto-fill, minmax(380px, 1fr))',
        gap: window.innerWidth <= 768 ? '20px' : '25px',
        padding: window.innerWidth <= 768 ? '0 5px' : '0'
      }}>
        {filteredNews.map(news => (
          <div
            key={news.id}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '20px' : '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f1f5f9',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            {/* Priority Indicator */}
            <div style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '4px',
              height: '100%',
              backgroundColor: getPriorityColor(news.priority)
            }} />

            {/* News Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FileText style={{
                  width: '20px',
                  height: '20px',
                  color: '#3b82f6'
                }} />
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#3b82f6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {news.category.replace('-', ' ')}
                </span>
              </div>
              <div style={{
                fontSize: '11px',
                color: '#9ca3af',
                backgroundColor: '#f8fafc',
                padding: '4px 8px',
                borderRadius: '12px',
                fontWeight: '500'
              }}>
                {news.priority.toUpperCase()}
              </div>
            </div>

            {/* News Title */}
            <h3 style={{
              fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.25rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '12px',
              lineHeight: '1.4'
            }}>
              {news.title}
            </h3>

            {/* News Description */}
            <p style={{
              fontSize: window.innerWidth <= 768 ? '0.9rem' : '0.95rem',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>
              {news.description}
            </p>

            {/* News Metadata */}
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'column',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: window.innerWidth <= 768 ? '0.825rem' : '0.875rem',
                color: '#6b7280'
              }}>
                <MapPin style={{ width: '14px', height: '14px' }} />
                <span>{news.location}</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: window.innerWidth <= 768 ? '0.825rem' : '0.875rem',
                color: '#6b7280'
              }}>
                <Calendar style={{ width: '14px', height: '14px' }} />
                <span>{formatDate(news.date)}</span>
              </div>
            </div>

            {/* Tags */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '16px'
            }}>
              {news.tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  style={{
                    fontSize: '0.75rem',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* News Source & Action */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '16px',
              borderTop: '1px solid #f1f5f9',
              flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap',
              gap: window.innerWidth <= 768 ? '10px' : '0'
            }}>
              <span style={{
                fontSize: window.innerWidth <= 768 ? '0.825rem' : '0.875rem',
                color: '#9ca3af',
                fontWeight: '500'
              }}>
                {news.source}
              </span>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#3b82f6',
                fontSize: window.innerWidth <= 768 ? '0.825rem' : '0.875rem',
                fontWeight: '600'
              }}>
                <span>Read More</span>
                <ExternalLink style={{ width: '14px', height: '14px' }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results Message */}
      {!loading && filteredNews.length === 0 && (
        <div style={{
          textAlign: 'center',
          marginTop: '60px',
          color: '#9ca3af',
          padding: window.innerWidth <= 768 ? '0 20px' : '0'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '16px'
          }}>📰</div>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '1.25rem' : '1.5rem',
            fontWeight: '600',
            marginBottom: '8px',
            color: '#64748b'
          }}>
            No news found
          </h3>
          <p style={{
            fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            Try adjusting your search terms or selecting a different category to find relevant RTO news and updates.
          </p>
        </div>
      )}

      {/* Loading Animation CSS */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Search;